import React, { useState, useEffect, Suspense, lazy, useCallback } from "react";
import DriftLayout from "../../../masterLayout/DriftLayout";
import { ToastContainer } from "react-toastify";
import { useLoader } from "../LoaderContext";
import { Spinner } from "react-bootstrap";
import { getOrderStatus } from "../../../DriftRoutes/contextsApi";

// Components
import OrderHeader from "./components/OrderHeader";
import OrderFilters from "./components/OrderFilters";
import OrderTable from "./components/OrderTable";
import SearchResultsTable from "./components/SearchResultsTable";

// Hooks
import { useOrders } from "./hooks/useOrders";
import { useOrderFilters } from "./hooks/useOrderFilters";
import { useOrderActions } from "./hooks/useOrderActions";
import { usePermissions } from "./hooks/usePermissions";

// Utils
import { formatDateToDDMMYYYY, renderDateDifference, truncateText } from "./utils/formatters";

// Lazy load modals for better performance
const CreateOrderModal = lazy(() => import("./modals/CreateOrderModal"));
const DeleteOrderModal = lazy(() => import("./modals/DeleteOrderModal"));
const QuantityModal = lazy(() => import("./modals/QuantityModal"));
const PartialDispatchModal = lazy(() => import("./modals/PartialDispatchModal"));
const DispatchRemarksModal = lazy(() => import("./modals/DispatchRemarksModal"));
const ProductionDetailsModal = lazy(() => import("./modals/ProductionDetailsModal"));
const MediaModal = lazy(() => import("./modals/MediaModal"));
const QueueModal = lazy(() => import("./modals/QueueModal"));
const LogsModal = lazy(() => import("./modals/LogsModal"));
const StatusLabelModal = lazy(() => import("./modals/StatusLabelModal"));

const OrderModuleNew = () => {
  const { showLoader, hideLoader } = useLoader();
  const permissions = usePermissions();

  // State for modals
  const [modalState, setModalState] = useState({
    showNewOrder: false,
    showDelete: false,
    showQuantity: false,
    showPartialDispatch: false,
    showDispatchRemarks: false,
    showProductionDetails: false,
    showMedia: false,
    showQueue: false,
    showLogs: false,
    showStatusLabel: false,
  });

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedQueues, setSelectedQueues] = useState([]);
  const [logsContent, setLogsContent] = useState([]);
  const [logsOrderId, setLogsOrderId] = useState("");
  const [editOrderData, setEditOrderData] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);

  const normalizeStatusValue = useCallback((value = "") => {
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
  }, []);

  // Custom hooks
  const {
    orders,
    setOrders,
    allProducts,
    setAllProducts,
    loading,
    searchLoading,
    totalPages,
    page,
    setPage,
    pageSize,
    setPageSize,
    nextOrderId,
    fetchOrders,
    searchOrders,
    refreshOrders,
  } = useOrders({ showLoader, hideLoader });

  const {
    searchField,
    setSearchField,
    searchValue,
    setSearchValue,
    statusFilter,
    setStatusFilter,
    quantityView,
    setQuantityView,
    expandQuantityView,
    setExpandQuantityView,
    showCurrentDateDiff,
    setShowCurrentDateDiff,
    dispatchDatesMap,
    setDispatchDatesMap,
    statusFilteredOrders,
    statusFilteredProducts,
    handleSort,
    handleSearch,
    clearSearch,
    debouncedSearch,
    sortField,
  } = useOrderFilters({
    orders,
    allProducts,
    fetchOrders,
    searchOrders
  });

  const {
    isProcessing,
    handleEditOrder,
    handleDeleteOrder,
    handleQuantity,
    handleStatusChange,
    handlePartialDispatchSave,
    handleDispatchDateSave,
    handleQueueSubmit,
    handleMediaPreview,
    handleProductClick,
    handleStatusLabelClick,
    saveUpdatedProductDetails,
    handleDeleteRow,
    handleDeleteMedia,
    handleViewLogs,
    handleDispatchRemarksSave
  } = useOrderActions({
    orders,
    setOrders,
    fetchOrders,
    refreshOrders,
    showLoader,
    hideLoader,
    setSelectedOrder,
    setSelectedProduct,
    setSelectedProductId,
    setSelectedQueues,
    setLogsContent,
    setLogsOrderId,
    setModalState,
    debouncedSearch,
    searchField,
    searchValue,
  });

  // Modal handlers
  const openModal = useCallback((modalName, order = null, product = null) => {
    if (order) setSelectedOrder(order);
    if (product) setSelectedProduct(product);
    setModalState(prev => ({ ...prev, [modalName]: true }));
  }, []);

  const closeModal = useCallback((modalName) => {
    setModalState(prev => ({ ...prev, [modalName]: false }));
    if (modalName === 'showNewOrder') {
      setEditOrderData(null);
    }
    if (modalName === 'showProductionDetails') {
      setSelectedProduct(null);
    }
    if (modalName === 'showDelete') {
      setSelectedOrder(null);
    }
    if (modalName === 'showQuantity') {
      setSelectedOrder(null);
    }
    if (modalName === 'showPartialDispatch') {
      setSelectedOrder(null);
    }
    if (modalName === 'showDispatchRemarks') {
      setSelectedOrder(null);
    }
    if (modalName === 'showMedia') {
      setSelectedProduct(null);
    }
    if (modalName === 'showQueue') {
      setSelectedProductId(null);
      setSelectedQueues([]);
    }
  }, []);

  const handleNewOrderSuccess = useCallback(() => {
    refreshOrders();
    closeModal('showNewOrder');
  }, [refreshOrders, closeModal]);

  const handleEditClick = useCallback(async (order) => {
    try {
      const editData = await handleEditOrder(order);
      setEditOrderData(editData);
      openModal('showNewOrder');
    } catch (error) {
      console.error("Failed to load edit data:", error);
    }
  }, [handleEditOrder, openModal]);

  const handleDeleteClick = useCallback((order) => {
    setSelectedOrder(order);
    openModal('showDelete');
  }, [openModal]);

  const handleQuantityClick = useCallback((order, type) => {
    setSelectedOrder(order);
    openModal('showQuantity');
  }, [openModal]);

  const handleStatusChangeWrapper = useCallback((e, index, order) => {
    handleStatusChange(e, index, order);
  }, [handleStatusChange]);

  const handleAddQueue = useCallback((order, productId, queues) => {
    setSelectedOrder(order);
    setSelectedProductId(productId);
    setSelectedQueues(queues || []);
    openModal('showQueue');
  }, [openModal]);

  const handleMediaPreviewWrapper = useCallback(async (product, orderId) => {
    setSelectedProduct(product);
    setSelectedOrder({ orderId });
    await handleMediaPreview(product, orderId);
    openModal('showMedia');
  }, [handleMediaPreview, openModal]);

  const handleStatusLabelClickWrapper = useCallback((order) => {
    setSelectedOrder(order);
    openModal('showStatusLabel');
  }, [openModal]);

  const handleViewLogsWrapper = useCallback((order) => {
    handleViewLogs(order);
  }, [handleViewLogs]);

  useEffect(() => {
    const loadStatusOptions = async () => {
      try {
        const response = await getOrderStatus();
        const apiOptions = Array.isArray(response?.data)
          ? response.data
              .map((item) => ({
                value: normalizeStatusValue(item?.name || ""),
                label: (item?.name || "").trim(),
              }))
              .filter((item) => item.value && item.label)
          : [];

        const uniqueOptions = apiOptions.filter(
          (item, index, all) =>
            index === all.findIndex((other) => other.value === item.value),
        );

        setStatusOptions(uniqueOptions);
      } catch (error) {
        console.error("Error loading order status options:", error);
      }
    };

    loadStatusOptions();
  }, [normalizeStatusValue]);


  // Determine which table to show
  const isSearchMode = (searchField === "Design_Name" || searchField === "Design_Colour") && searchValue;
  const isQuantitySearch = searchField === "Quantity" && searchValue;
  const isLoading = loading || searchLoading;

  return (
    <DriftLayout>
      <ToastContainer />

      <div className="card mt-4">
        <OrderHeader
          permissions={permissions}
          onNewOrder={() => openModal('showNewOrder')}
        />

        <div className="card-body">
          <OrderFilters
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            searchField={searchField}
            onSearchFieldChange={setSearchField}
            searchValue={searchValue}
            onSearchChange={handleSearch}
            onClearSearch={clearSearch}
            sortField={sortField}
            onSort={handleSort}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            orders={orders}
            statusOptions={statusOptions}
            showCurrentDateDiff={showCurrentDateDiff}
            onToggleDateDiff={() => setShowCurrentDateDiff(!showCurrentDateDiff)}
          />

          {isSearchMode ? (
            <SearchResultsTable
              products={statusFilteredProducts}
              loading={isLoading}
              quantityView={quantityView}
              onQuantityViewChange={setQuantityView}
              dispatchDatesMap={dispatchDatesMap}
              onDispatchDateChange={(orderId, date) => {
                console.log("Dispatch date changed:", orderId, date);
                setDispatchDatesMap(prev => ({ ...prev, [orderId]: date }));
              }}
              onSaveDispatchDate={(orderId, date, id) => {
                console.log("Saving dispatch date:", orderId, date, id);
                handleDispatchDateSave(orderId, date, id);
              }}
              savingDispatchId={isProcessing.savingDispatchId}
              dispatchDatePermission={permissions.dispatchDate}
              onProductClick={handleProductClick}
              onAddQueue={handleAddQueue}
              onMediaPreview={handleMediaPreviewWrapper}
              truncateText={truncateText}
              formatDate={formatDateToDDMMYYYY}
              renderDateDifference={renderDateDifference}
              showCurrentDateDiff={showCurrentDateDiff}
              statusFilter={statusFilter}
            />
          ) : (
            <OrderTable
              orders={statusFilteredOrders}
              loading={isLoading}
              page={page}
              pageSize={pageSize}
              quantityView={quantityView}
              onQuantityViewChange={setQuantityView}
              expandQuantityView={expandQuantityView}
              onExpandQuantityViewChange={setExpandQuantityView}
              dispatchDatesMap={dispatchDatesMap}
              onDispatchDateChange={(orderId, date) => {
                console.log("Dispatch date changed:", orderId, date);
                setDispatchDatesMap(prev => ({ ...prev, [orderId]: date }));
              }}
              onSaveDispatchDate={(orderId, date, id) => {
                console.log("Saving dispatch date:", orderId, date, id);
                handleDispatchDateSave(orderId, date, id);
              }}
              savingDispatchId={isProcessing.savingDispatchId}
              dispatchDatePermission={permissions.dispatchDate}
              actions={permissions.actions}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onQuantity={handleQuantityClick}
              onStatusChange={handleStatusChangeWrapper}
              onProductClick={handleProductClick}
              onAddQueue={handleAddQueue}
              onMediaPreview={handleMediaPreviewWrapper}
              onViewLogs={handleViewLogsWrapper}
              onStatusLabelClick={handleStatusLabelClickWrapper}
              truncateText={truncateText}
              formatDate={formatDateToDDMMYYYY}
              renderDateDifference={renderDateDifference}
              showCurrentDateDiff={showCurrentDateDiff}
              statusFilter={statusFilter}
              statusOptions={statusOptions}
            />
          )}

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              <span>Page {page} of {totalPages}</span>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <label htmlFor="pageSelect" className="text-sm">Page:</label>
              <select
                id="pageSelect"
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
                className="px-3 py-2 border rounded text-primary"
              >
                {Array.from({ length: totalPages }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
              <span className="text-sm">of {totalPages}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Lazy loaded modals */}
      <Suspense fallback={<div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>}>
        {modalState.showNewOrder && (
          <CreateOrderModal
            show={modalState.showNewOrder}
            onHide={() => closeModal('showNewOrder')}
            onSuccess={handleNewOrderSuccess}
            editData={editOrderData}
            isEditMode={!!editOrderData}
            nextOrderId={nextOrderId}
          />
        )}

        {modalState.showDelete && selectedOrder && (
          <DeleteOrderModal
            show={modalState.showDelete}
            onHide={() => closeModal('showDelete')}
            order={selectedOrder}
            onDelete={handleDeleteOrder}
            loading={isProcessing.deleting}
          />
        )}

        {modalState.showQuantity && selectedOrder && (
          <QuantityModal
            show={modalState.showQuantity}
            onHide={() => closeModal('showQuantity')}
            order={selectedOrder}
            onSave={handleQuantity}
            loading={isProcessing.saving}
          />
        )}

        {modalState.showPartialDispatch && selectedOrder && (
          <PartialDispatchModal
            show={modalState.showPartialDispatch}
            onHide={() => closeModal('showPartialDispatch')}
            order={selectedOrder}
            totalQuantity={selectedOrder?.quantityInPcs}
            onSave={handlePartialDispatchSave}
            loading={isProcessing.partialDispatch}
          />
        )}

        {modalState.showDispatchRemarks && selectedOrder && (
          <DispatchRemarksModal
            show={modalState.showDispatchRemarks}
            onHide={() => closeModal('showDispatchRemarks')}
            order={selectedOrder}
            onSave={handleDispatchRemarksSave}
            loading={isProcessing.saving}
          />
        )}

        {modalState.showProductionDetails && selectedProduct && (
          <ProductionDetailsModal
            show={modalState.showProductionDetails}
            onHide={() => closeModal('showProductionDetails')}
            product={selectedProduct}
            onSave={saveUpdatedProductDetails}
            onDelete={handleDeleteRow}
            user={permissions.user}
            loading={isProcessing.saving}
          />
        )}

        {modalState.showMedia && selectedOrder && (
          <MediaModal
            show={modalState.showMedia}
            onHide={() => closeModal('showMedia')}
            orderId={selectedOrder?.orderId}
            product={selectedProduct}
            onDelete={handleDeleteMedia}
            loading={isProcessing.loading}
          />
        )}

        {modalState.showQueue && selectedOrder && (
          <QueueModal
            show={modalState.showQueue}
            onHide={() => closeModal('showQueue')}
            order={selectedOrder}
            productId={selectedProductId}
            selectedQueues={selectedQueues}
            onQueueChange={setSelectedQueues}
            onSubmit={handleQueueSubmit}
            loading={isProcessing.queue}
          />
        )}

        {modalState.showLogs && (
          <LogsModal
            show={modalState.showLogs}
            onHide={() => closeModal('showLogs')}
            logs={logsContent}
            orderId={logsOrderId}
          />
        )}

        {modalState.showStatusLabel && selectedOrder && (
          <StatusLabelModal
            show={modalState.showStatusLabel}
            onHide={() => closeModal('showStatusLabel')}
            order={selectedOrder}
          />
        )}
      </Suspense>
    </DriftLayout>
  );
};

export default OrderModuleNew;